import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  customDateFormatter,
  intl,
  intlFmt,
  NotificationType,
  toast
} from '../../lib';
import { OtpInput } from './OtpInput';
import { InlineError, Maskable } from '../basic/';
import { isNumericString, removeWhiteSpaces } from '../../util';

enum SendingState {
  NotSent,
  Sending,
  Sent
}

const initializeInputValues = (lengthOfCode: number) => {
  return Array.from({ length: lengthOfCode }, () => '#');
};

type ErrorObj = {
  isError: boolean;
  errorMsg: string | undefined;
};

function createNoErrorObj(): ErrorObj {
  return {
    isError: false,
    errorMsg: undefined
  };
}

function ResendTimer({
  onTimeout
}: Readonly<{
  onTimeout: () => void;
}>): React.JSX.Element {
  const [timeLeft, setTimeLeft] = useState(90);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      setTimeLeft(prevTimeLeft => {
        if (prevTimeLeft === 0) {
          clearInterval(intervalIdRef.current!);
          onTimeoutRef.current();
          return 0;
        }
        return prevTimeLeft - 1;
      });
    }, 1000);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const printableTime = customDateFormatter({
    second: '2-digit',
    minute: 'numeric'
  }).formatDate(new Date(timeLeft * 1000));

  return (
    <p className="otp-resend-timer font-16">
      {intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeResendTimer',
        printableTime
      )}
    </p>
  );
}

function OtpConfirmationBoundary({
  masked,
  children
}: Readonly<{
  masked: boolean;
  children: React.JSX.Element;
}>): React.JSX.Element {
  return (
    <div className="ehs-otp-confirmation-control">
      <Maskable
        masked={masked}
        className="authentication-content-loading-spinner"
      >
        {children}
      </Maskable>
    </div>
  );
}

export type OtpChannel = 'SMS' | 'EMAIL' | 'TOTP';
export type OtpDetails = {
  sendMethod: OtpChannel;
  recipient: string;
};

export const OtpConfirmationControl = function OtpConfirmationControl({
  autoFocusFirstInput,
  onOtpValid,
  lengthOfCode,
  validateAuthenticationCodeCall,
  sendAuthenticationCodeCall,
  initialOtpDetails,
  disabled = false,
  ref
}: Readonly<{
  autoFocusFirstInput?: boolean;
  onOtpValid: () => void;
  lengthOfCode: number;
  validateAuthenticationCodeCall: (
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void,
    onRequestStatusChanged: (pending: boolean) => void
  ) => void;
  sendAuthenticationCodeCall: (
    onSendingSuccess: (sendDetails: OtpDetails) => void,
    onSendingError: (msg?: string, resendDisabled?: boolean) => void
  ) => void;
  initialOtpDetails?: OtpDetails;
  disabled?: boolean;
}> & { ref?: React.Ref<unknown> }): React.JSX.Element {
  const initialState = initialOtpDetails
    ? SendingState.Sent
    : SendingState.NotSent;
  const [sendingState, setSendingState] = useState(initialState);
  const [sendingError, setSendingError] = useState<ErrorObj>(
    createNoErrorObj()
  );
  const [hasValidationError, setHasValidationError] = useState<ErrorObj>(
    createNoErrorObj()
  );
  const [authenticationCode, setAuthenticationCode] = useState(
    initializeInputValues(lengthOfCode)
  );
  const [isConfirmRequestPending, setIsConfirmRequestPending] = useState(false);
  const [isResendDisabledAfterError, setIsResendDisabledAfterError] = useState(
    false
  );
  const [showResendTimer, setShowResendTimer] = useState(false);
  const initialSendingState = useMemo(() => {
    return (
      initialOtpDetails || {
        sendMethod: 'TOTP' as OtpChannel,
        recipient: ''
      }
    );
  }, [initialOtpDetails]);
  const [sendingDetails, setSendingDetails] = useState(initialSendingState);
  const resendTimerEnabled = sendingDetails.sendMethod !== 'TOTP';
  const [isTanSent, setIsTanSent] = useState(false);

  useEffect(() => {
    if (sendingState === SendingState.Sent && isTanSent) {
      setShowResendTimer(resendTimerEnabled);
    }
  }, [sendingState, resendTimerEnabled, isTanSent]);

  // Expose the reset method via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      setSendingState(initialState);
      setSendingError(createNoErrorObj());
      setHasValidationError(createNoErrorObj());
      setAuthenticationCode(initializeInputValues(lengthOfCode));
      setIsConfirmRequestPending(false);
      setIsResendDisabledAfterError(false);
      setShowResendTimer(false);
      setSendingDetails(initialSendingState);
    }
  }));

  const triggerResend = () => {
    setSendingState(SendingState.NotSent);
    setHasValidationError(createNoErrorObj());
    setSendingError(createNoErrorObj());
    setIsResendDisabledAfterError(false);
  };

  const getActivationCodeSentToastMessage = (
    sendingDetails: OtpDetails
  ): string | undefined => {
    if (sendingDetails.sendMethod === 'EMAIL') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaEmailToast',
        sendingDetails.recipient
      );
    } else if (sendingDetails.sendMethod === 'SMS') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaSmsToast',
        sendingDetails.recipient
      );
    }
    return undefined;
  };

  const getActivationCodeSentDescription = (
    sendingDetails: OtpDetails,
    lengthOfCode: number
  ): string => {
    if (sendingDetails.sendMethod === 'EMAIL') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeEmailDescription',
        lengthOfCode,
        sendingDetails.recipient
      );
    } else if (sendingDetails.sendMethod === 'SMS') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSmsDescription',
        lengthOfCode,
        sendingDetails.recipient
      );
    } else if (sendingDetails.sendMethod === 'TOTP') {
      return intl('loginhook.twofactor.view.form.otp.app');
    }
    return '';
  };

  const validateAuthenticationCode = (authenticationCode: string) => {
    validateAuthenticationCodeCall(
      removeWhiteSpaces(authenticationCode),
      () => onOtpValid(),
      (errorMsg?) => {
        setAuthenticationCode(initializeInputValues(lengthOfCode));
        setHasValidationError({
          isError: true,
          errorMsg: errorMsg
        });
      },
      (pending: boolean) => setIsConfirmRequestPending(pending)
    );
  };

  useEffect(() => {
    if (
      sendingState === SendingState.Sent &&
      sendingDetails.recipient.length > 0
    ) {
      const toastMsg = getActivationCodeSentToastMessage(sendingDetails);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      toastMsg && toast(toastMsg, NotificationType.SUCCESS, undefined);
    }
  }, [sendingState, sendingDetails]);

  // Render function
  if (sendingState === SendingState.Sent) {
    const showResendLink =
      resendTimerEnabled &&
      !isResendDisabledAfterError &&
      !isConfirmRequestPending &&
      !showResendTimer;
    return (
      <OtpConfirmationBoundary masked={isConfirmRequestPending}>
        <div className="ehs-otp-confirmation-control-content">
          <p
            className="activation-code-description font-16"
            id="activation-code-sent-description"
          >
            {getActivationCodeSentDescription(sendingDetails, lengthOfCode)}
          </p>
          {hasValidationError.isError && (
            <InlineError
              title={intl(
                'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedTitle'
              )}
              text={
                hasValidationError.errorMsg ??
                intl(
                  'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedText'
                )
              }
            />
          )}
          <OtpInput
            autoFocusFirstInput={autoFocusFirstInput}
            authenticationCode={authenticationCode}
            lengthOfCode={lengthOfCode}
            onChange={authCode => {
              setHasValidationError(createNoErrorObj());
              setAuthenticationCode(authCode);

              const currentCode = authCode.join('');

              if (
                currentCode.length >= lengthOfCode &&
                isNumericString(currentCode)
              ) {
                validateAuthenticationCode(currentCode);
                return true;
              }
              return false;
            }}
            hasError={hasValidationError.isError}
            ariaLabelOfDigit="patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.enterDigitHint"
            ariaDescribedByIdOfDigit="activation-code-sent-description"
            onBadCodePasted={() => {
              toast(
                intl(
                  'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedTitle'
                ),
                NotificationType.WARN,
                undefined
              );
            }}
            disabled={disabled}
          />
          {showResendLink && (
            <p className="otp-resend-link-container font-16">
              {intl(
                'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeTakingTooLong'
              )}
              &nbsp;
              <button
                className="otp-resend-link"
                onClick={() => triggerResend()}
                onKeyUp={e => {
                  if (e.key === ' ') {
                    triggerResend();
                  }
                }}
              >
                {intl(
                  'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSendAgain'
                )}
              </button>
            </p>
          )}
          {showResendTimer && (
            <ResendTimer
              onTimeout={() => {
                setShowResendTimer(false);
              }}
            />
          )}
        </div>
      </OtpConfirmationBoundary>
    );
  } else if (sendingError.isError) {
    return (
      <OtpConfirmationBoundary masked={isConfirmRequestPending}>
        <InlineError
          title={intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCodeFailedTitle'
          )}
          text={
            sendingError.errorMsg ??
            intl(
              'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCodeFailedText'
            )
          }
        />
      </OtpConfirmationBoundary>
    );
  } else {
    if (sendingState === SendingState.NotSent) {
      setSendingState(SendingState.Sending);
      sendAuthenticationCodeCall(
        (sendDetails: OtpDetails) => {
          setSendingDetails(sendDetails);
          setSendingState(SendingState.Sent);
          setIsTanSent(true);
        },
        (msg?: string, resendDisabled?: boolean) => {
          if (resendDisabled) {
            setIsResendDisabledAfterError(true);
          }
          setSendingError({
            isError: true,
            errorMsg: msg
          });
        }
      );
    }

    return (
      <OtpConfirmationBoundary masked={true}>
        <p className="font-16">
          {intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCode'
          )}
        </p>
      </OtpConfirmationBoundary>
    );
  }
};
